import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Overline, FadeIn } from "@/components/site/Shared";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const projectTypes = ["Residential Construction", "Commercial Construction", "Industrial Construction", "Government Project", "Roads & Infrastructure", "Renovation & Remodeling", "Interior Works", "Architectural Design", "Structural Design", "BOQ & Quantity Surveying", "Project Management", "Material Supply", "Turnkey Construction"];
const budgets = ["Under ₹50 Lakh", "₹50 Lakh – ₹2 Cr", "₹2 Cr – ₹10 Cr", "₹10 Cr – ₹50 Cr", "Above ₹50 Cr", "Prefer not to say"];

export default function RequestQuote() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", company: "", project_type: "", location: "", budget: "", start_date: "", description: "" });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_type) { toast.error("Please select a project type"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/quote", fd);
      setReference(data.reference);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit request");
    } finally {
      setSending(false);
    }
  };

  if (reference) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center px-5" data-testid="quote-success">
        <div className="border border-line bg-graphite p-12 max-w-xl text-center">
          <CheckCircle2 size={52} className="text-emerald-400 mx-auto mb-6" />
          <h1 className="font-heading text-3xl uppercase tracking-tight text-white">Request Received</h1>
          <p className="text-silver mt-4 leading-relaxed">Your quotation request has been logged in our ERP. A project consultant will contact you within 24 hours.</p>
          <div className="border border-ember/40 bg-ember/10 px-6 py-4 mt-8">
            <p className="text-xs uppercase tracking-[0.2em] text-silver">Your Reference</p>
            <p className="font-heading text-2xl text-ember font-semibold mt-1" data-testid="quote-reference">{reference}</p>
          </div>
          <Link to="/" className="inline-block mt-8 text-white uppercase text-xs tracking-[0.2em] font-bold border-b-2 border-ember pb-2 hover:text-ember transition-colors duration-200">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="request-quote-page">
      <section className="py-24 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <Overline className="mb-5">Request a Quote</Overline>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter text-white leading-[0.95]">
              A Detailed Quotation<br />Within <span className="text-ember">72 Hours</span>
            </h1>
            <p className="text-silver max-w-2xl mt-8 leading-relaxed">
              Share your requirement and drawings. Our estimation desk prepares an itemised, measurable quotation — with milestone-wise payment schedule — and your dedicated consultant walks you through every line.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <form onSubmit={submit} className="border border-line bg-graphite p-8 sm:p-12" data-testid="quote-form">
            <h2 className="font-heading text-xl uppercase tracking-tight text-white mb-8">Your Details</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Full Name *</label>
                <Input required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="quote-name-input" className="rounded-none bg-carbon border-line h-12" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Phone *</label>
                <Input required value={form.phone} onChange={set("phone")} placeholder="+91" data-testid="quote-phone-input" className="rounded-none bg-carbon border-line h-12" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Email *</label>
                <Input required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="quote-email-input" className="rounded-none bg-carbon border-line h-12" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Company (Optional)</label>
                <Input value={form.company} onChange={set("company")} placeholder="Company name" data-testid="quote-company-input" className="rounded-none bg-carbon border-line h-12" />
              </div>
            </div>

            <h2 className="font-heading text-xl uppercase tracking-tight text-white mt-12 mb-8">Project Details</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Project Type *</label>
                <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                  <SelectTrigger className="rounded-none bg-carbon border-line h-12" data-testid="quote-project-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent className="rounded-none bg-graphite border-line">
                    {projectTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Location *</label>
                <Input required value={form.location} onChange={set("location")} placeholder="City / Site location" data-testid="quote-location-input" className="rounded-none bg-carbon border-line h-12" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Budget Range</label>
                <Select value={form.budget} onValueChange={(v) => setForm({ ...form, budget: v })}>
                  <SelectTrigger className="rounded-none bg-carbon border-line h-12" data-testid="quote-budget-select"><SelectValue placeholder="Select budget" /></SelectTrigger>
                  <SelectContent className="rounded-none bg-graphite border-line">
                    {budgets.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Expected Start Date</label>
                <Input type="date" value={form.start_date} onChange={set("start_date")} data-testid="quote-date-input" className="rounded-none bg-carbon border-line h-12" />
              </div>
            </div>
            <div className="mt-6">
              <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Project Description *</label>
              <Textarea required value={form.description} onChange={set("description")} rows={5} placeholder="Plot size, floors, specifications, timeline expectations…" data-testid="quote-description-input" className="rounded-none bg-carbon border-line" />
            </div>

            <div className="mt-8">
              <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Upload Drawings / BOQ / Photos (Optional)</label>
              <label className="border border-dashed border-line bg-carbon flex flex-col items-center justify-center py-10 cursor-pointer hover:border-ember transition-colors duration-200" data-testid="quote-file-upload">
                <Upload size={24} className="text-ember mb-3" />
                <span className="text-silver text-sm">{files.length > 0 ? `${files.length} file(s) selected` : "Click to attach files (PDF, DWG, images — max 10MB each)"}</span>
                <input type="file" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files))} />
              </label>
            </div>

            <Button type="submit" disabled={sending} data-testid="quote-submit-btn"
              className="mt-10 w-full rounded-none bg-ember hover:bg-[#e64a19] text-white uppercase tracking-[0.15em] font-bold h-14 text-sm">
              {sending ? "Submitting…" : "Submit Quote Request"}
            </Button>
            <p className="text-silver/70 text-xs mt-4 text-center">Your request creates a lead in our ERP — nothing gets lost, and you get a trackable reference number.</p>
          </form>
        </div>
      </section>
    </div>
  );
}
